// ENTRY-TYPE EXPECTATION PROFILES (R5.8) — CVCRAFT_R5_8_SPEC_entry_type_calibration.md rev 2; codebook §2.2b.
//
// WHAT THIS IS. A deterministic, closed table from (entry_type × ledger sub-type) to WHERE A STATEMENT IS
// CONVENTIONALLY EXPECTED on a Canadian medical CV. The construct is unchanged — CVCraft still measures
// documentation sufficiency (§1.1) — but "not stated" is only SAID, ASKED, or COUNTED AGAINST an entry where
// convention expects the statement. A clinical post conventionally states title, institution and dates; it does
// not state a "contribution", a count, or a deliverable, and a review that asks a clinical post for those is not
// smart about what a medical CV is (owner live-smoke finding, 2026-08-22).
//
// WHAT IT READS — AND WHAT IT MUST NOT. Inputs are `entry_type`, `clinical.exposure_type`, `work.work_type`,
// `frequency`, `is_ongoing`, `date_start`, `date_end`, and the PRESENCE of the entry's own `entry_type` fact.
// Every one of these is a band input already held fixed across paraphrases and across name/institution swaps
// (§11.1 / §11.2), so a profile is paraphrase- and name-invariant BY CONSTRUCTION. It MUST NOT read `raw_text`,
// `role_title`, `responsibility_statement`, `institution`, `source_section`, or any signal fact — a profile that
// branched on wording would be a second, hidden band. `leadership_role`'s duty-text gate is the one place a
// profile CONSUMER (`buildFlags`) reads `responsibility_statement`; it reads PRESENCE only, as it always has.
//
// WHO READS A PROFILE (invariant I-CAL, spec §2.5, rev 2):
//   1. `buildFlags`            — a flag the profile does not allow is NOT CONSTRUCTED (no orphan scope, C2b);
//   2. `buildRecommendations`  — a question the profile does not allow is NOT CONSTRUCTED;
//   3. `buildUnits`            — OQ-1 (decided 2026-08-22): a facet the profile marks `not_expected` does not
//                                count AGAINST the unit in §4.2 banding — D1 is satisfied BY CONVENTION where
//                                Responsibility is not expected, D2 where neither Completion nor Continuity is
//                                expected. The true facet LEVEL is unchanged and stored; only the element verdict
//                                moves, and only in the satisfying direction, and only on those profiles;
//   4. the `expectations` projection on `EntryAssessment` — so the surface can render `CS-FACET-NA` for a
//                                not-expected facet at raw L1 (and the real level whenever the CV states one).
// Nothing else: no facet level, no descriptor, no confidence rule, no presentation band, no §6b overlay row.
//
// NO FACT, NO PROFILE. A non-generic, non-fallback profile applies only when the entry carries a terminal
// `entry_type` fact (extraction mints one for every entry — spanned, or `closed_world` "other"; a user edit writes
// a `user_supplied` one). An entry whose type fact was `user_flagged_wrong` is stripped by `excludeFlaggedFacts`
// before the engine reads it and therefore falls back to `uncategorised` — the same fail-closed reading every
// other rule takes. The fact is what the conventional element verdicts CITE.
//
// THE FALLBACK RULE (owner directive, 2026-08-23 — closes the entry-type routing gap, launch blocker #1). Every
// case the resolver cannot place into a real profile — no `entry_type` fact, `entry_type === 'other'` (Education,
// Certification, Membership, Language, Award), an unresolvable clinical subtype, or an undated
// teaching/service/volunteer-clinical span — now routes to `uncategorised` (or `clinical_unspecified` when the
// entry is at least known clinical), NOT to `generic`. `generic`'s ALL_FLAGS/ALL_QUESTIONS allowance used to be
// silently re-granted to these entries, including the project-contribution critique
// (`CS-FLAG-CONTRIBUTION-NOT-STATED`) on a degree, a certification, or an award — the owner's finding that review
// "was not smart in knowing that medical cv does not need metrics or specific details in certain roles or
// duties". `uncategorised` fails closed on FLAGS AND QUESTIONS — it allows neither (Sol, 2026-08-23 round 2:
// restricting only the flags left an untyped entry still receiving a dates question it may have no convention
// for). `clinical_unspecified` keeps `CS-Q-DATES` and the one flag that never assumed a subtype, because we do
// at least know that entry is clinical.
//
// AND BOTH FALLBACKS CARRY THE THIRD FACET STATE, `unknown` — which is the part that took two review rounds to
// get right. Restricting only the flags and questions left the facts panel still printing "Role not stated" and
// "Status not stated" on a degree: an omission claim against a convention nobody established. Flipping the rows
// to `not_expected` instead would have waived §4.2 banding elements for every unplaceable entry (OQ-1), because
// `buildUnits` reads `not_expected` as "documented by convention" — and waiving an element for an AWARD, whose
// convention is known, is not the same claim as waiving it for an entry we merely failed to classify. `unknown`
// is the state that does neither: it waives nothing and it renders nothing. See `FacetExpectation`.
//
// `education` AND `language` ARE NOW REAL MEMBERS of `EntryType` with their own N/N/N profiles, so the two
// commonest entries that used to be unplaceable are PLACED rather than merely excused. NOTE THE GATE: the
// extractor does not yet EMIT either value — `fact_ledger_entries.entry_type`'s CHECK constraint has not been
// widened on the remote D1 (`scripts/gen-migration-0026.mjs`, and `T-ENUM-DB-1` pins that the two sets can never
// drift apart). Until that migration is applied, such entries still arrive as `other` and take `uncategorised`,
// where the honest output is silence rather than a critique — which is the owner's complaint fixed either way.
//
// `generic` itself is unchanged and stays in `PROFILE_TABLE` for the pre-v7 stored-read path (`GENERIC_EXPECTATIONS`) and any other
// module that names it directly; only the resolver below stopped returning it.

import {
  NOT_STATED,
  type ClinicalEntryRecord,
  type EntryRecord,
  type EntryType,
  type ExposureType,
  type NotStated,
  type WorkRecord,
} from './factLedger';
import { terminalFact } from './terminalFacts';

/**
 * WHAT CONVENTION SAYS ABOUT A FACET ON THIS ENTRY — three states, and the third one is load-bearing.
 *
 *   `expected`     — convention expects the statement here. A missing statement is an L1 level, said out loud.
 *   `not_expected` — convention does NOT expect it here (an award states no "contribution"). The surface glosses
 *                    the L1 level as `CS-FACET-NA`, and §4.2 treats the element as documented BY CONVENTION.
 *   `unknown`      — WE DO NOT KNOW WHAT CONVENTION EXPECTS, because we could not place the entry at all. Added
 *                    2026-08-23 on Sol's prescription, adopted by owner delegation.
 *
 * WHY `unknown` COULD NOT JUST BE ONE OF THE OTHER TWO. `expected` makes the surface print "Role not stated" on a
 * degree — an omission claim against a convention nobody established, which is the launch blocker Sol raised.
 * `not_expected` says convention positively does not expect it, which we also do not know, AND §4.2 reads it as
 * satisfying a banding element by convention — so an unplaceable entry would silently earn band credit. Waiving
 * an element for an AWARD (whose convention is known and states nothing about contribution) is a different claim
 * from waiving it for an entry we simply failed to classify. That distinction is the whole reason for this state.
 *
 * SO `unknown` DOES NEITHER: it never satisfies a §4.2 element (`buildUnits` waives on `not_expected` only, and
 * says so explicitly rather than by falling through), and it renders NOTHING at all — no level, no `CS-FACET-NA`,
 * no row (`facetRenders`). Silence is the only honest output for a question we did not answer, and it is also
 * what the owner asked for: do not print convention claims the applicant cannot act on. The true facet LEVEL is
 * still computed and still stored on the payload; only the CLAIM is withheld.
 */
export type FacetExpectation = 'expected' | 'not_expected' | 'unknown';

/** The three flags `buildFlags` can raise (§8.2). The other five fire elsewhere and are not profile-gated. */
export type ProfileFlagType = 'status_not_stated' | 'contribution_not_stated' | 'clinical_role_context_not_stated';

/** The three questions `buildRecommendations` can ask (§8.2 / §10.3). */
export type ProfileQuestionId = 'CS-Q-DATES' | 'CS-Q-DURATION' | 'CS-Q-CLINICAL-CONTEXT';

export type ProfileId =
  | 'clinical_training'
  | 'clinical_volunteer_standing'
  | 'clinical_volunteer_one_off'
  | 'clinical_employment'
  | 'research_project'
  | 'scholarly_work'
  | 'leadership_role'
  | 'service_standing'
  | 'service_one_off'
  | 'teaching_standing'
  | 'teaching_single'
  | 'employment'
  | 'award'
  | 'certification'
  | 'membership'
  | 'education'
  | 'language'
  | 'generic'
  | 'uncategorised'
  | 'clinical_unspecified';

/** The dated-span reading that splits standing from one-off roles (spec §1 note c, rev 2). */
export type DatedSpanKind = 'standing' | 'single' | 'undated';

export interface ProfileRow {
  profile_id: ProfileId;
  responsibility: FacetExpectation;
  completion: FacetExpectation;
  continuity: FacetExpectation;
  flags_allowed: readonly ProfileFlagType[];
  questions_allowed: readonly ProfileQuestionId[];
  /**
   * `leadership_role` only (Sol ruling): a named office with no duty text is NOT auto-flagged for a missing
   * contribution; the flag is confined to entries whose duty text omits what the applicant was responsible for.
   */
  contribution_flag_requires_duty_text: boolean;
  /** `clinical_employment` only (OQ-2): `CS-Q-CLINICAL-CONTEXT` is offered only where independent practice is stated. */
  clinical_context_question_requires_stated_independence: boolean;
  /** `employment` only: `CS-Q-DURATION` is offered only where the entry states no dated span. */
  duration_question_requires_no_dated_span: boolean;
  /**
   * DOES CONVENTION EXPECT THIS ENTRY TO STATE AN END AT ALL? (2026-08-23, live regression on the owner's CV.)
   *
   * `CS-Q-DATE-END` ("Your entry states one date. If this ran over a period, adding when it ended — or that it
   * is ongoing — makes that clear.") is only worth ASKING where the entry might describe a SPAN whose end is
   * unknown. Its wording no longer asserts that it does — that was a separate 2026-08-23 fix, because extraction
   * stores every lone date as `date_start` whatever it means — but asking it at all still presumes span-ness. Most CV entries are not spans. A Licentiate of the Medical Council of Canada dated 2023 is a
   * POINT-IN-TIME, PERMANENT credential; asking when it ended is a category error, and the phrasing actively
   * implies the licence lapses — a worse failure than the membership one, because it misinforms rather than
   * merely bores. Live: the Certifications section went from 0 points to 8, every one of them this question.
   *
   * THIS IS A SEPARATE FLAG ON PURPOSE — it is NOT inferable from the facet triple. Continuity-`expected` is
   * about whether a repeated span is documented, a different question: it would exclude `education` (a degree
   * genuinely runs 2014–2021 and its row is `[N, N, N]`) while including rows that state no end. Convention about
   * SPAN-NESS gets its own column or it gets silently derived from something that does not mean it.
   *
   * DEFAULTS FALSE — fail-closed, the same reading every other rule here takes. A row that has not thought about
   * span-ness does not ask, and a new row added later must opt IN. A profile with this FALSE and a start-only
   * date is asked NOTHING about dates: not the end question, and not the generic one either, because the entry
   * already states the date its convention expects.
   */
  end_date_question_applies: boolean;
}

const ALL_FLAGS: readonly ProfileFlagType[] = ['status_not_stated', 'contribution_not_stated', 'clinical_role_context_not_stated'];
const ALL_QUESTIONS: readonly ProfileQuestionId[] = ['CS-Q-DATES', 'CS-Q-DURATION', 'CS-Q-CLINICAL-CONTEXT'];

function row(
  profile_id: ProfileId,
  facets: [FacetExpectation, FacetExpectation, FacetExpectation],
  flags_allowed: readonly ProfileFlagType[],
  questions_allowed: readonly ProfileQuestionId[],
  extra: Partial<Pick<ProfileRow,
    'contribution_flag_requires_duty_text' | 'clinical_context_question_requires_stated_independence'
    | 'duration_question_requires_no_dated_span' | 'end_date_question_applies'>> = {},
): ProfileRow {
  return {
    profile_id,
    responsibility: facets[0],
    completion: facets[1],
    continuity: facets[2],
    flags_allowed,
    questions_allowed,
    contribution_flag_requires_duty_text: false,
    clinical_context_question_requires_stated_independence: false,
    duration_question_requires_no_dated_span: false,
    // FAIL-CLOSED DEFAULT — see the field's doc. A row must opt IN to being asked when it ended.
    end_date_question_applies: false,
    ...extra,
  };
}

const E: FacetExpectation = 'expected';
const N: FacetExpectation = 'not_expected';
/** `unknown` — see `FacetExpectation`. Neither a band waiver nor a printable claim. */
const U: FacetExpectation = 'unknown';

/**
 * THE TABLE — spec §1 rev 2, Claude+Sol rulings of 2026-08-22, encoded once. Order: Responsibility / Completion /
 * Continuity. `generic` is today's behaviour and is what every unresolvable case falls back to.
 */
export const PROFILE_TABLE: Readonly<Record<ProfileId, ProfileRow>> = {
  // Dates + setting suffice. No contribution, count, deliverable, or duration expectation.
  clinical_training: row('clinical_training', [N, N, N], ['clinical_role_context_not_stated'], ['CS-Q-DATES'],
    { end_date_question_applies: true }),  // a rotation/elective runs from a date to a date
  // volunteer_clinical is treated like service: standing vs one-off by dated span.
  // 2026-08-23 — `duration_question_requires_no_dated_span` added here for the reason it exists on `employment`:
  // a DATED standing post ("Volunteer clinic assistant, 2023-01 – 2024-01") carries its own answer to "how long",
  // so asking is asking the applicant to retype their header. That is precisely the owner's complaint ("the asking
  // for metrics thing still persists… the app should be smart enough to know that"). `service_standing` and
  // `teaching_standing` deliberately do NOT take this flag: for those, "how often" is genuinely conventional and
  // the dates do not answer it.
  clinical_volunteer_standing: row('clinical_volunteer_standing', [E, N, E], ['clinical_role_context_not_stated'], ['CS-Q-DATES', 'CS-Q-DURATION'],
    { duration_question_requires_no_dated_span: true, end_date_question_applies: true }),
  // ONE-OFF: no end to state — a single occasion's date IS its whole date. `end_date_question_applies` stays false.
  clinical_volunteer_one_off: row('clinical_volunteer_one_off', [E, N, N], ['clinical_role_context_not_stated'], ['CS-Q-DATES']),
  // OQ-2: the clinical-context question only where independent practice is stated (signal-gated in the consumer).
  clinical_employment: row('clinical_employment', [N, N, N], ['clinical_role_context_not_stated'], ['CS-Q-DATES', 'CS-Q-CLINICAL-CONTEXT'],
    { clinical_context_question_requires_stated_independence: true, end_date_question_applies: true }),
  research_project: row('research_project', [E, E, E], ['contribution_not_stated'], ['CS-Q-DATES', 'CS-Q-DURATION'],
    { end_date_question_applies: true }),  // a project runs over a period
  // A citation states status/venue/authorship; the contribution belongs to a linked project entry.
  // A PUBLICATION HAS A YEAR, NOT A SPAN. "When did this paper end?" is not a question, so the flag stays false.
  scholarly_work: row('scholarly_work', [N, E, N], ['status_not_stated'], ['CS-Q-DATES']),
  // 2026-08-23 — `duration_question_requires_no_dated_span` ADDED (finding accepted by the coordinating session).
  // "President, Student Society, 2021–2022" is the exact shape on the owner's CV, and it was being asked "How long
  // or how often was this?" although its own header answers it. That is the identical retype-your-own-header
  // defect T-METRIC-4 closed for `clinical_volunteer_standing` and that `employment` was always gated for. An
  // UNDATED leadership role still gets the ask — the gate is the dated span, never the profile.
  leadership_role: row('leadership_role', [E, E, E], ['contribution_not_stated'], ['CS-Q-DATES', 'CS-Q-DURATION'],
    { contribution_flag_requires_duty_text: true, duration_question_requires_no_dated_span: true, end_date_question_applies: true }),
  // 2026-08-23 — GATED, AND THE SPLIT FROM `teaching_standing` IS THE POINT. DO NOT "SIMPLIFY" THESE TWO BACK
  // TOGETHER: they look alike and they are not.
  //   * SERVICE: a dated committee appointment ("Member, Curriculum Committee, 2022–2024") does NOT conventionally
  //     state its meeting frequency on a Canadian medical CV, and its dates already answer "how long". So a dated
  //     service entry is asked nothing about duration — same gate as `employment` and `leadership_role`.
  //   * TEACHING (below): a stated teaching LOAD genuinely is conventional ("weekly small-group tutorials",
  //     "8 sessions per term"). The dates of a teaching post do NOT answer how often you taught, so the ask
  //     survives there even on a dated span, and `teaching_standing` deliberately keeps `false`.
  // The two rows differ because the conventions differ, not because one was overlooked.
  service_standing: row('service_standing', [E, N, E], [], ['CS-Q-DATES', 'CS-Q-DURATION'],
    { duration_question_requires_no_dated_span: true, end_date_question_applies: true }),
  // ONE-OFF / SINGLE: no end to state. Flag stays false for both of these, and for `teaching_single` below.
  service_one_off: row('service_one_off', [E, N, N], [], ['CS-Q-DATES']),
  // KEEPS THE UNGATED DURATION ASK ON PURPOSE — see the `service_standing` comment above for the full argument.
  // A teaching post's dates do not state its teaching load, and a load is conventional here.
  teaching_standing: row('teaching_standing', [E, E, E], [], ['CS-Q-DATES', 'CS-Q-DURATION'],
    { end_date_question_applies: true }),
  teaching_single: row('teaching_single', [E, N, N], [], ['CS-Q-DATES']),
  employment: row('employment', [N, N, E], [], ['CS-Q-DATES', 'CS-Q-DURATION'],
    { duration_question_requires_no_dated_span: true, end_date_question_applies: true }),
  // AWARD / CERTIFICATION: a POINT-IN-TIME credential or honour. An LMCC dated 2023 is permanent, and asking
  // when it "ended" both misreads the entry and implies to the applicant that the credential lapses. This is the
  // live regression of 2026-08-23 (Certifications went 0 → 8 points, all of them this question). Flag stays FALSE:
  // a certification stating only "2023" is asked NOTHING about dates, because 2023 is the date its convention
  // expects. The generic "When did this take place?" is unaffected — it stays coherent for a point-in-time event.
  award: row('award', [N, N, N], [], ['CS-Q-DATES']),
  certification: row('certification', [N, N, N], [], ['CS-Q-DATES']),
  // 2026-08-23 — `CS-Q-DATES` REMOVED FROM `membership`, and this row is now the ONLY typed row that asks nothing
  // at all besides `language`. THE RULING (Claude, under owner delegation; owner's live-CV finding is the case).
  //
  // A MEMBERSHIP IS A STANDING STATE, NOT AN EVENT. "Member, SOGC" / "Fellow, WACS" / "Member, AIMGA" is how a
  // Canadian medical CV conventionally states one: the organization, and at most a role within it. There is no
  // conventionally expected year — a membership is read as CURRENT unless the CV says otherwise, and a great many
  // real medical CVs list memberships with no date anywhere in the section. `CS-Q-DATES` asks "When did this take
  // place?", which presupposes an EVENT with a placement in time. A standing affiliation did not "take place".
  //
  // THIS IS THE §2.2b PRINCIPLE APPLIED, NOT AN EXCEPTION TO IT. §2.2b says "not stated" may only be SAID, ASKED or
  // COUNTED AGAINST an entry where CONVENTION EXPECTS THE STATEMENT. Convention does not expect a membership year,
  // so the question may not be constructed — exactly as `award` may not be asked for a contribution. Nothing about
  // the construct moved; we are only declining to ask for a statement no reader expects to find.
  //
  // THE OWNER'S LIVE CASE IS WHAT MAKES IT URGENT. On the owner's CV today, Professional Memberships holds three
  // undated entries (AIMGA, SOGC, WACS) and the ENTIRE review of that section was the same date question three
  // times, promoted to the section's "Do this first" banner. A section whose whole feedback is one boilerplate
  // question repeated per row is the owner's "not smart about what a medical CV is" complaint in its purest form:
  // it is not a review, it is a form field. With this row emptied the section produces silence, which is the
  // honest output — a fully conventional membership list has nothing a reviewer would ask it to document.
  //
  // WHY `certification`, `education` AND `award` KEEP THE QUESTION (the line is drawn, not erased). A credential
  // carries a year and the year is MATERIAL: certifications expire (BLS 2024 vs BLS 2019 is a different claim
  // about current competence), an exam year places the applicant in a training timeline (USMLE Step 1 2022), a
  // degree conventionally states its years, and an award's year is part of how the award is read. Each of those is
  // a DATED EVENT OR A DATED VALIDITY WINDOW; a membership is neither. The distinction is convention, not mercy.
  membership: row('membership', [N, N, N], [], []),
  // 2026-08-23 — REAL TYPES FOR THE TWO THINGS THAT USED TO BE UNPLACEABLE. A degree and a language proficiency
  // are the commonest entries on a medical CV that `EntryType` had no member for, so both arrived as `other` and
  // were told they "describe the project but not what you were responsible for". A degree conventionally states
  // the credential, the institution and the years — no contribution, no deliverable, no duration ask beyond the
  // dates it already carries. A language proficiency conventionally states the language and the level, and
  // nothing else at all: it takes no questions, because "how long or how often was this?" is meaningless about a
  // language you speak.
  // EDUCATION IS THE ONE `[N, N, N]` ROW THAT IS A REAL SPAN (MD 2014–2021), which is exactly why span-ness needed
  // its own column instead of being read off the facet triple.
  education: row('education', [N, N, N], [], ['CS-Q-DATES'], { end_date_question_applies: true }),
  language: row('language', [N, N, N], [], []),
  // `generic` means "no profile logic has run" (pre-v7 stored reads) and allows everything by definition, so it
  // opts in here too rather than becoming the one maximal row with a hole in it. The resolver never returns it.
  generic: row('generic', [E, E, E], ALL_FLAGS, ALL_QUESTIONS, { end_date_question_applies: true }),
  // FALLBACK — an entry whose type is UNKNOWN or explicitly `other` (Education, Membership, Language, an award or
  // certification the extractor left untyped, no `entry_type` fact at all, or an undated service/teaching span).
  //
  // IT IS FAIL-CLOSED ON ALL THREE CHANNELS, and Sol's 2026-08-23 review is why all three had to move together.
  // The first cut restricted only the FLAGS and left `[E, E, E]` + `CS-Q-DATES` standing, on the reasoning that
  // banding must not move. That was half a fix: an entry with no known convention still received a dates question
  // it might have no convention for, and its facts panel still printed "Role not stated", "Status not stated" and
  // an unestablished dated span — three negative-looking verdicts about a convention nobody had established. The
  // surface then showed that entry GREEN in the section index (no flags) above three red-reading facet claims.
  //
  // `unknown` CLOSES IT, and it is neither of the two obvious repairs. `expected` prints the omission claim;
  // `not_expected` asserts convention positively does not expect the statement AND earns the entry a §4.2 element
  // for free (OQ-1). `unknown` waives nothing and renders nothing — see `FacetExpectation` for the full argument.
  //
  // THE UPSTREAM FIX IS HALF LANDED: `education` and `language` are real `EntryType` members with real profiles
  // above, but the extractor cannot emit them until the D1 CHECK is widened (see the banner). So a degree still
  // reaches this row today, and this row is still what keeps the review from telling it that it "describes the
  // project but not what you were responsible for" (owner, 2026-08-22: "it was not smart in knowing that medical
  // cv does not need metrics or specific details in certain roles or duties").
  uncategorised: row('uncategorised', [U, U, U], [], []),
  // Fallback for a clinical entry with no clinical record or an unstated `exposure_type`, and for an undated
  // volunteer_clinical span. We know the entry IS clinical, just not which subtype — so it keeps the one flag
  // that never assumed a subtype (S4) and the dates question, which every clinical profile allows. Its facet
  // expectations stay `[E, E, E]` for the same banding reason as `uncategorised` above: the clinical profiles it
  // might have resolved to are `[N, N, N]`, but taking their shape on a subtype we did NOT establish would waive
  // banding elements on an assumption. Same open defect, same owner-scope repair.
  clinical_unspecified: row('clinical_unspecified', [U, U, U], ['clinical_role_context_not_stated'], ['CS-Q-DATES']),
};

export const PROFILE_IDS: readonly ProfileId[] = Object.keys(PROFILE_TABLE) as ProfileId[];

/** The resolved profile for one entry: the table row plus the sub-type that selected it and the fact ids behind it. */
export interface ExpectationProfile extends ProfileRow {
  /** The ledger sub-type that selected the row: an `exposure_type`, a `work_type`, a dated-span kind, or `not_stated`. */
  sub_type: string;
  /** The fact ids a conventional element verdict cites — the entry's `entry_type` fact and, where present, the sub-type fact. */
  evidence_fact_ids: string[];
}

/** What the engine STORES on each `EntryAssessment` (projected to the surface verbatim). */
export interface EntryExpectations {
  profile_id: ProfileId;
  sub_type: string;
  responsibility: FacetExpectation;
  completion: FacetExpectation;
  continuity: FacetExpectation;
}

function isStated<T>(v: T | NotStated): v is T {
  return v !== NOT_STATED && v !== undefined && v !== null && v !== '';
}

/**
 * Standing vs single vs undated — from the LEDGER, never from title words (spec §1 note c, rev 2).
 *
 *   standing — a stated frequency other than `one_off`, OR `is_ongoing` true, OR a stated start and end that differ;
 *   single   — a stated `one_off` frequency, OR dated (start stated) but not standing (start only, or start = end);
 *   undated  — no date, no ongoing flag, no frequency. Sol: "do NOT classify undated as single" — an undated
 *              teaching or service entry keeps today's behaviour (generic profile) so a standing role the CV forgot
 *              to date is never quietly excused its duration question.
 */
export function datedSpanKind(entry: EntryRecord): DatedSpanKind {
  if (isStated(entry.frequency) && entry.frequency !== 'one_off') return 'standing';
  if (entry.is_ongoing === true) return 'standing';
  const start = isStated(entry.date_start) ? entry.date_start : null;
  const end = isStated(entry.date_end) ? entry.date_end : null;
  if (start !== null && end !== null && start !== end) return 'standing';
  if (entry.frequency === 'one_off') return 'single';
  if (start !== null || end !== null) return 'single';
  return 'undated';
}

/** A stated dated span in the §4.2 D2(d) sense: start + end, or start + ongoing. */
export function hasDatedSpan(entry: EntryRecord): boolean {
  const start = isStated(entry.date_start);
  return (start && isStated(entry.date_end)) || (start && entry.is_ongoing === true);
}

const TRAINING_EXPOSURES: readonly ExposureType[] = ['observership', 'elective', 'core_rotation', 'selective', 'simulation'];

/**
 * Resolve the expectation profile for one entry. Pure; reads only the fields the banner names.
 *
 * FALLBACK RULE (owner directive 2026-08-23, closing the entry-type routing gap — launch blocker #1): an
 * uncategorised or unresolvable entry is NOT the same as a full, untyped one. `generic`'s ALL_FLAGS /
 * ALL_QUESTIONS allowance was minted for "no profile logic has run yet" (pre-R5.8 behaviour), and it was silently
 * re-granting the project-contribution critique (`contribution_not_stated`) to Education, Certification,
 * Membership, Language and Award entries whenever they extracted as `entry_type: other`, and to undated
 * teaching/service entries whose OWN typed profile explicitly forbids that flag. That is not smart about what a
 * medical CV conventionally states in those places (owner: "it was not smart in knowing that medical cv does not
 * need metrics or specific details in certain roles or duties"). So every unresolvable case below now routes to
 * `uncategorised` (or `clinical_unspecified` when we at least know the entry is clinical) instead of `generic`:
 * facet expectations stay IDENTICAL to `generic` (banding must not move — OQ-1), but the flag/question allowance
 * is fail-closed to nothing but CS-Q-DATES, plus the ungated clinical-context flag where the entry is known
 * clinical. `generic` itself is unchanged and stays in the table for the pre-v7 stored-read path and other
 * modules that reference it directly; only this resolver stopped returning it.
 */
export function expectationProfile(
  entry: EntryRecord,
  clinical: ClinicalEntryRecord | null,
  work: WorkRecord | null,
): ExpectationProfile {
  const typeFact = terminalFact(entry.facts, 'entry_type');
  const uncategorised = (sub_type: string): ExpectationProfile => ({
    ...PROFILE_TABLE.uncategorised,
    sub_type,
    evidence_fact_ids: typeFact ? [typeFact.fact_id] : [],
  });
  const clinicalUnspecified = (sub_type: string): ExpectationProfile => ({
    ...PROFILE_TABLE.clinical_unspecified,
    sub_type,
    evidence_fact_ids: typeFact ? [typeFact.fact_id] : [],
  });
  if (typeFact === null) return uncategorised('no_entry_type_fact');

  const typeId = typeFact.fact_id;
  const resolved = (id: ProfileId, sub_type: string, extraFactIds: (string | null)[] = []): ExpectationProfile => ({
    ...PROFILE_TABLE[id],
    sub_type,
    evidence_fact_ids: [typeId, ...extraFactIds.filter((x): x is string => x !== null)],
  });

  const type: EntryType = entry.entry_type;
  switch (type) {
    case 'clinical': {
      if (clinical === null || !isStated(clinical.exposure_type)) return clinicalUnspecified('not_stated');
      const exposureFactId = terminalFact(entry.facts, 'clinical.exposure_type')?.fact_id ?? null;
      const exposure = clinical.exposure_type;
      if (TRAINING_EXPOSURES.includes(exposure)) return resolved('clinical_training', exposure, [exposureFactId]);
      if (exposure === 'employment_clinical') return resolved('clinical_employment', exposure, [exposureFactId]);
      // volunteer_clinical — treated like service, split by dated span.
      const span = datedSpanKind(entry);
      if (span === 'standing') return resolved('clinical_volunteer_standing', `${exposure}:standing`, [exposureFactId]);
      if (span === 'single') return resolved('clinical_volunteer_one_off', `${exposure}:one_off`, [exposureFactId]);
      return clinicalUnspecified(`${exposure}:undated`);
    }
    case 'research_project':
      return resolved('research_project', 'research_project');
    case 'scholarly_work': {
      const workType = work === null ? 'work_type_not_stated' : work.work_type;
      return resolved('scholarly_work', workType);
    }
    case 'leadership_role':
      return resolved('leadership_role', 'leadership_role');
    case 'service': {
      const span = datedSpanKind(entry);
      if (span === 'standing') return resolved('service_standing', 'standing');
      if (span === 'single') return resolved('service_one_off', 'one_off');
      return uncategorised('undated');
    }
    case 'teaching': {
      const span = datedSpanKind(entry);
      if (span === 'standing') return resolved('teaching_standing', 'standing');
      if (span === 'single') return resolved('teaching_single', 'single');
      return uncategorised('undated');
    }
    case 'employment':
      return resolved('employment', hasDatedSpan(entry) ? 'dated_span' : 'no_dated_span');
    case 'award':
      return resolved('award', 'award');
    case 'certification':
      return resolved('certification', 'certification');
    case 'membership':
      return resolved('membership', 'membership');
    case 'education':
      return resolved('education', 'education');
    case 'language':
      return resolved('language', 'language');
    case 'other':
      return uncategorised('other');
    default: {
      const exhaustive: never = type;
      return uncategorised(String(exhaustive));
    }
  }
}

/** The stored projection of a resolved profile. */
export function toEntryExpectations(p: ExpectationProfile): EntryExpectations {
  return {
    profile_id: p.profile_id,
    sub_type: p.sub_type,
    responsibility: p.responsibility,
    completion: p.completion,
    continuity: p.continuity,
  };
}

/** The generic expectations a stored read from BEFORE `SCORING_VERSION` 7 is read under (no `expectations` field). */
export const GENERIC_EXPECTATIONS: EntryExpectations = {
  profile_id: 'generic',
  sub_type: 'pre_v7_read',
  responsibility: 'expected',
  completion: 'expected',
  continuity: 'expected',
};

/**
 * The three facets' L1 levels, by facet key — the only levels `CS-FACET-NA` may replace, and (C2b, rubricEngine
 * `attachFacetScope`) the only facet decisions that carry a per-entry closed-world `search_scope`. ONE table for
 * both rules, so "is this an L1" can never be answered two ways.
 */
export const FACET_L1_LEVEL = {
  responsibility: 'role_not_stated',
  completion: 'status_not_stated',
  continuity: 'single_occasion_or_repeated_span_not_established',
} as const;

export type FacetKey = keyof typeof FACET_L1_LEVEL;

/**
 * THE ONE RENDERING RULE (spec §3 rev 2): `CS-FACET-NA` iff the facet is `not_expected` AND the raw level is L1.
 * A stated level (≥ L2) always renders as itself — convention is a floor on what we ask for, not a ceiling on what
 * we credit. A read with no `expectations` (stored before v7) renders as today. Returns null when the facet's own
 * copy id should render.
 */
/**
 * THE SECOND RENDERING RULE (2026-08-23) — DOES THIS FACET RENDER AT ALL?
 *
 * `unknown` renders NOTHING: no level label, no `CS-FACET-NA`, no row. We could not place the entry, so we have
 * no convention to measure it against, and every available label would state something we did not establish —
 * "Role not stated" asserts an omission against an unknown convention, and `CS-FACET-NA` asserts that convention
 * positively does not expect it. Silence is the only honest output, and it is what the owner asked for: do not
 * print convention claims the applicant cannot act on.
 *
 * ONLY the L1 case is withheld. A facet the entry actually STATES (level ≥ L2) is a fact about the document, not
 * a claim about convention, so it renders normally even on an unplaceable entry — the applicant wrote it, and we
 * are only reporting it back. A read stored before this state existed carries no `unknown` and renders as today.
 */
export function facetRenders(
  expectations: EntryExpectations | null | undefined,
  facet: FacetKey,
  level: string,
): boolean {
  if (!expectations) return true;
  return !(expectations[facet] === 'unknown' && level === FACET_L1_LEVEL[facet]);
}

export function facetNotExpectedCopyId(
  expectations: EntryExpectations | null | undefined,
  facet: FacetKey,
  level: string,
): 'CS-FACET-NA' | null {
  if (!expectations) return null;
  return expectations[facet] === 'not_expected' && level === FACET_L1_LEVEL[facet] ? 'CS-FACET-NA' : null;
}
